class BlogPredictiveSearch extends HTMLElement {
	constructor() {
		super();

		// refs
		this.input = null;
		this.resultsWrapper = null;
		this.blogHandle = this.getAttribute('blog-handle') || null;
		this.blogTitle = this.getAttribute('blog-title') || null;
		this.abortController = null;
		this.currentLocale = 'en';
		this.debounceDelay = 250;
		this.debouncedOnInput = null;

		// Localized text
		this.noResultsTitle = this.dataset.noResultsTitle || 'Oops! No articles found.';
		this.noResultsHint = this.dataset.noResultsHint || 'But don\'t worry — the world is full of amazing journeys waiting for you.';
		this.preciseResultsText = this.dataset.preciseResults || 'Precise Results';
		this.relatedResultsText = this.dataset.relatedResults || 'Related Results';

		// Additional Search and Clear Search Button
		this.searchEverBtn = null;
		this.clearSearchBtn = null;


		// bind methods
		this.onInput = this.onInput.bind(this);
		this.fetchResults = this.fetchResults.bind(this);
		this.renderResults = this.renderResults.bind(this);
	}

	connectedCallback() {
		this.searchEverBtn = document.querySelector('#searchEverBtn');
		this.clearSearchBtn = document.querySelector('.clear-btn');

		this.resultsWrapper = this.querySelector('.results-wrapper');
		this.input = this.querySelector('input[type="search"]');

		if (!this.input || !this.resultsWrapper) {
			console.error('[BlogPredictiveSearch] Missing <input> or .results container.');
			return;
		}

		// determine current locale
		this.detectCurrentLocale();

		this.debouncedOnInput = this.debounce(this.onInput, this.debounceDelay);
		this.input.addEventListener('input', this.debouncedOnInput);

		// 2. Attach listener to button
		if (this.searchEverBtn) {
			this.searchEverBtn.addEventListener('click', (e) => {
				e.preventDefault();
				this.redirectToSearch(this.input);
			});
		}

		// 3. (Optional) Attach Enter key event to input for better UX
		if (this.input) {
			this.input.addEventListener('keypress', (e) => {
				if (e.key === 'Enter') {
					e.preventDefault();
					this.redirectToSearch(this.input);
				}
			});
		}

		// Apply Backdrop / outside click
		if (this.resultsWrapper) {
			document.addEventListener('click', (e) => {
				// if click is outside search container (wrapper + input + controls)
				const searchContainer = document.querySelector('.search-container');

				if (searchContainer && !searchContainer.contains(e.target)) {
					this.clearInput();
				}
			});
		}



		//  Attach listener to button
		if (this.clearSearchBtn) {
			this.clearSearchBtn.addEventListener('click', (e) => {
				e.preventDefault();
				this.clearInput();
			});
		}
	}

	disconnectedCallback() {
		if (this.input && this.debouncedOnInput) {
			this.input.removeEventListener('input', this.debouncedOnInput);
		}
	}

	debounce(fn, delay) {
		let timer;
		return (...args) => {
			window.clearTimeout(timer);
			timer = window.setTimeout(() => fn(...args), delay);
		};
	}

	async onInput(e) {
		const query = e.target.value.trim();

		if (!query) {
			this.clearSearchBtn.classList.add('hidden'); // always hide when query is empty
			this.resultsWrapper.innerHTML = '';
			return;
		}

		this.clearSearchBtn.classList.remove('hidden'); // show clear button

		if (query.length < 2) {
			this.resultsWrapper.innerHTML = '';
			return;
		}

		// cancel previous request
		if (this.abortController) this.abortController.abort();
		this.abortController = new AbortController();

		try {
			const results = await this.fetchResults(query, this.abortController.signal);
			this.renderResults(results);
		} catch (err) {
			if (err.name !== 'AbortError') {
				console.error('[BlogPredictiveSearch] Error fetching results:', err);
			}
		}
	}

	constructUrl(url) {

		// Only prepend locale if it's defined and not 'en'
		if (this.currentLocale && this.currentLocale !== 'en') {
			// Ensure url starts with /
			let cleanUrl = url.startsWith('/') ? url : `/${url}`;
			return `/${this.currentLocale}${cleanUrl}`;
		}

		// Default return
		return url.startsWith('/') ? url : `/${url}`;
	}



	async fetchResults(query, signal) {
		// Locales Shopify's /search/suggest does not support: use the local article index (assets/i18n-search.js)
		if (window.TVI18nSearch) return window.TVI18nSearch.searchArticles(query, 10);

		const urlEndpoint = `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=article`;
		const endpoint = this.constructUrl(urlEndpoint);

		const response = await fetch(endpoint, { signal });

		if (!response.ok) throw new Error(`HTTP ${response.status}`);
		const data = await response.json();



		const articles = data.resources.results.articles || [];

		// return all articles
		return articles;
	}


	renderResults(articles) {
		if (!articles.length) {
			this.resultsWrapper.innerHTML = `
    <div class="no-results">
	<div class='not-found-img-container'>
		<img
		src="https://cdn.shopify.com/s/files/1/0847/5679/1592/files/not-found-img.png?v=1758099573"
		alt="No articles found"
		class="no-results-img"
		/>
	</div>
      <p>
        ${this.noResultsTitle}<br>
        <span class="hint">${this.noResultsHint}</span>
      </p>
    </div>
  `;
			return;
		}


		let exactResult = [];
		let relatedResults = [];

		// Separate exact vs related
		articles.forEach(article => {
			const blogHandle = this.blogHandleOf(article.url); // e.g. "dubai"

			if (blogHandle === this.blogHandle) {
				exactResult.push(article);
			} else {
				relatedResults.push(article);
			}
		});

		let exactResultMarkup = ``;
		let relatedResultMarkup = ``;

		// Exact results
		if (exactResult.length > 0) {
			const allItemsMarkup = exactResult.map(article =>
				this.getSearchItemMarkup(article, 'exact-item')
			).join('');

			exactResultMarkup = `
			<div class="exact-match-container">
				<h3 class="search-item-heading exact-search-heading">${this.preciseResultsText}</h3>
				<div class="exact-results">
				${allItemsMarkup}
				</div>
			</div>
   			 `;
		}

		// Related results
		if (relatedResults.length > 0) {
			const allItemsMarkup = relatedResults.map(article =>
				this.getSearchItemMarkup(article, 'related-item')
			).join('');

			relatedResultMarkup = `
			<div class="related-results-container">
				<h3 class="search-item-heading related-search-heading">${this.relatedResultsText}</h3>
				<div class="related-results">
				${allItemsMarkup}
				</div>
			</div>
    `;
		}

		// Combine into wrapper
		this.resultsWrapper.innerHTML = `
    ${exactResultMarkup}
    ${exactResultMarkup && relatedResultMarkup ? `<div class="category-divider"></div>` : ``}
    ${relatedResultMarkup}
  `;
	}

	// Blog handle from an article URL, with or without a locale prefix: /blogs/dubai/x and /ar/blogs/dubai/x -> "dubai".
	// Translated handles arrive percent-encoded (/ar/blogs/%D9%83...), while blog.handle in Liquid is decoded, so decode.
	blogHandleOf(url) {
		const parts = String(url || '').split('?')[0].split('/');
		const i = parts.indexOf('blogs');
		if (i < 0 || !parts[i + 1]) return '';
		try {
			return decodeURIComponent(parts[i + 1]).trim();
		} catch (e) {
			return parts[i + 1].trim();
		}
	}

	// Blog title for a handle, from the blog picker's options (data-handle / data-value, both in the active language)
	blogTitleOf(handle) {
		const option = Array.from(document.querySelectorAll('.select-options li[data-handle]')).find(
			(li) => li.dataset.handle === handle
		);
		return (option && option.dataset.value) || handle;
	}

	getSearchItemMarkup(article, cssClass) {

		// Default is exact blog title
		let currentBlogTitle = this.blogTitle;
		let cssBadgeClass = 'exact-badge';
		if (cssClass === 'related-item') {
			cssBadgeClass = 'related-badge'
			currentBlogTitle = this.blogTitleOf(this.blogHandleOf(article.url)); // e.g. "Dubai"
		}



		return `
    <a href="${article.url}" class="result-item ${cssClass}">
      <div class="result-thumb">
        ${article.featured_image?.url
				? `<img src="${article.featured_image.url}" alt="${article.title}" />`
				: `<div class="placeholder-thumb"></div>`}
      </div>
      <div class="result-content">
        <h4 class="result-title">${article.title}</h4>

        	<div class="item-details">
			${article.published_at ? `<span class="result-date">${new Date(article.published_at).toLocaleDateString()}</span>`
				: ''}
				<span class="blog blog-title-badge ${cssBadgeClass}">${currentBlogTitle}</span>
         	</div>
      </div>
    </a>
  `;
	}

	// 1. Function to handle search redirect
	redirectToSearch(queryInput) {
		const query = queryInput.value.trim();
		if (!query) return; // skip if empty

		const encodedQuery = encodeURIComponent(query);
		const urlEndpoint = `/search?q=${encodedQuery}&options[prefix]=last`;
		const url = this.constructUrl(urlEndpoint);

		window.location.href = url;
	}

	// Clear Input Search and perform other management
	clearInput() {
		// clear input
		this.input.value = '';
		this.resultsWrapper.innerHTML = '';
		this.clearSearchBtn.classList.add('hidden')
	}
	detectCurrentLocale() {
		const currentUrl = window.location.pathname;
		const urlSegments = currentUrl?.split('/').filter(Boolean); // parse out empty segments

		// Match 2-letter locales (ar, fr) and hyphenated ones (pt-BR, zh-CN)
		if (urlSegments.length > 0 && /^[a-z]{2}(-[a-z]{2})?$/i.test(urlSegments[0])) {
			this.currentLocale = urlSegments[0];
		}
	}


}

// register custom element
customElements.define('blog-predictive-search', BlogPredictiveSearch);